// src/utils/tenantPortalContent.js
/**
 * Utility to manage customizable public school portal content
 * (image slider metadata, school achievements, upcoming events, announcements, and school info).
 * Scoped by tenant/subdomain to guarantee strict multi-tenant isolation.
 */

const PORTAL_STORAGE_PREFIX = "classnx_tenant_portal_";

export const DEFAULT_PORTAL_CATEGORIES = [
  "Academic",
  "Sports",
  "Arts & Culture",
  "STEM & Innovation",
  "Community",
  "Notice",
];

export const getDefaultPortalContent = (schoolName = "School Campus", subdomain = "") => {
  const name = schoolName || (subdomain ? subdomain.charAt(0).toUpperCase() + subdomain.slice(1) : "School Campus");
  return {
    tagline: "Inspiring Excellence, Fostering Innovation, Shaping Tomorrow",
    welcomeTitle: `Welcome to ${name}`,
    welcomeMessage: `${name} is dedicated to fostering academic excellence, integrity, and lifelong learning. Our holistic curriculum empowers students to discover their passions, master emerging skills, and lead in a rapidly changing world.`,
    establishedYear: "2015",
    affiliation: "CBSE & International Curriculum Affiliated",
    contactEmail: `contact@${subdomain || "school"}.classnx.com`,
    contactPhone: "+91 (080) 4567-8900",
    campusAddress: "Main Campus Boulevard, Knowledge Park District",
    officeHours: "Monday – Saturday: 8:00 AM – 4:30 PM",
    stats: [
      { id: "stat-1", number: "100%", label: "Board Exam Pass Rate" },
      { id: "stat-2", number: "14:1", label: "Student-Faculty Ratio" },
      { id: "stat-3", number: "35+", label: "Clubs & Sports Teams" },
      { id: "stat-4", number: "100%", label: "Digital Smart Classrooms" },
    ],
    achievements: [
      {
        id: "ach-1",
        title: "National Science & Robotics Olympiad 2025",
        category: "STEM & Innovation",
        year: "2025",
        statNumber: "1st Place",
        statLabel: "Gold Trophy",
        description: "Our senior robotics team secured first place among 450+ top regional institutions for autonomous solar rover design.",
      },
      {
        id: "ach-2",
        title: "100% First-Class Board Exam Distinctions",
        category: "Academic",
        year: "2025",
        statNumber: "98.4%",
        statLabel: "Top Score",
        description: "Outstanding academic results across Science and Humanities streams with 52 students securing over 95% aggregate marks.",
      },
      {
        id: "ach-3",
        title: "State Inter-School Athletics Championship",
        category: "Sports",
        year: "2025",
        statNumber: "18 Medals",
        statLabel: "Overall Champions",
        description: "Victorious in Track & Field, Swimming, and Basketball tournaments, setting three state-level youth records.",
      },
      {
        id: "ach-4",
        title: "Green Campus & Environmental Leadership Award",
        category: "Community",
        year: "2024",
        statNumber: "Eco-Grade A+",
        statLabel: "National Honor",
        description: "Recognized for zero-waste campus initiatives, rooftop solar energy integration, and student-led afforestation drives.",
      },
    ],
    events: [
      {
        id: "evt-1",
        title: "Annual STEM & Arts Innovation Expo 2026",
        category: "STEM & Innovation",
        date: "2026-10-24",
        time: "09:30 AM – 03:30 PM",
        venue: "Central Campus Pavilion",
        badge: "Flagship Event",
        summary: "Interactive science projects, coding displays, fine arts gallery, and student musical presentations open to all families.",
      },
      {
        id: "evt-2",
        title: "Parent-Teacher Collaborative Conference",
        category: "Community",
        date: "2026-11-08",
        time: "08:30 AM – 01:30 PM",
        venue: "Academic Block & Hybrid Virtual",
        badge: "Important",
        summary: "One-on-one reviews of mid-term student portfolios, attendance milestones, and growth targets for the upcoming semester.",
      },
      {
        id: "evt-3",
        title: "Inter-House Athletic & Sports Championship",
        category: "Sports",
        date: "2026-11-18",
        time: "08:00 AM – 05:00 PM",
        venue: "Campus Sports Arena",
        badge: "Sports",
        summary: "Track events, football and basketball finals, followed by the presentation of the Annual Rolling Champions Trophy.",
      },
      {
        id: "evt-4",
        title: "Academic Year 2026–27 Admissions Open",
        category: "Notice",
        date: "2026-12-01",
        time: "Online 24/7",
        venue: "Online Admissions Portal",
        badge: "Admissions",
        summary: "Early online applications open for Kindergarten through Grade 11. Campus tour bookings and brochure downloads available.",
      },
    ],
  };
};

export const getTenantPortalStorageKey = (subdomainOrId = "") => {
  const safeKey = String(subdomainOrId || "default")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_");
  return `${PORTAL_STORAGE_PREFIX}${safeKey}`;
};

export const loadTenantPortalContent = (subdomainOrId = "", schoolName = "") => {
  const key = getTenantPortalStorageKey(subdomainOrId);
  const fallback = getDefaultPortalContent(schoolName, subdomainOrId);

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      ...fallback,
      ...parsed,
      stats: Array.isArray(parsed?.stats) && parsed.stats.length > 0 ? parsed.stats : fallback.stats,
      achievements: Array.isArray(parsed?.achievements) && parsed.achievements.length > 0 ? parsed.achievements : fallback.achievements,
      events: Array.isArray(parsed?.events) && parsed.events.length > 0 ? parsed.events : fallback.events,
    };
  } catch {
    return fallback;
  }
};

export const saveTenantPortalContent = (subdomainOrId = "", content = {}) => {
  const key = getTenantPortalStorageKey(subdomainOrId);
  try {
    window.localStorage.setItem(key, JSON.stringify(content));
    window.dispatchEvent(
      new CustomEvent("school-portal:updated", {
        detail: { tenantId: subdomainOrId },
      })
    );
    return true;
  } catch {
    return false;
  }
};
